// Custom Sentry issue-grouping for errors thrown by the Medusa JS SDK.
//
// The SDK (`@medusajs/js-sdk`) throws a single generic `Error` for *every*
// non-2xx response, all from the same frame in `client.ts`. Sentry's default
// grouping fingerprints on the stack trace, so unrelated failures — an expected
// 401 `Unauthorized` on `GET /account` and a 400 `Account holder … already
// exists` on `POST /checkout` — collapse into ONE issue (the throw-site), whose
// displayed title is just whatever the latest representative event happened to
// be. That makes a single issue un-actionable and means a `Fixes` keyword on
// one bug silently resolves the others.
//
// The fix: give SDK errors an explicit fingerprint keyed on the HTTP status
// (the load-bearing discriminator — 401 vs 400) plus a *normalized* message, so
// the same logical error groups together regardless of the dynamic IDs in its
// text, but distinct errors stay distinct. The fingerprint deliberately omits
// `{{ default }}` — including it would re-mix the stack trace back in and
// re-collapse everything onto `client.ts`.

type HttpLikeError = {
  status: number;
  message?: unknown;
  statusText?: unknown;
};

// An SDK / HTTP-shaped error carries a numeric `status`. `isAuthRejection` in
// src/lib/data/customer.ts relies on the same shape, so this matches what the
// SDK actually throws.
function isHttpLikeError(error: unknown): error is HttpLikeError {
  return (
    error !== null &&
    typeof error === 'object' &&
    'status' in error &&
    typeof (error as { status: unknown }).status === 'number'
  );
}

// Pick the text to fingerprint on: the error's own message, falling back to
// `statusText` when it's missing/non-string. The Medusa SDK FetchError shape
// carries `statusText`, and `medusaError()` (src/lib/util/medusa-error.ts)
// leans on the same fallback. Without it, distinct statusless errors sharing a
// status would collapse back into one group.
function rawErrorMessage(error: HttpLikeError): string {
  if (typeof error.message === 'string') {
    return error.message;
  }
  if (typeof error.statusText === 'string') {
    return error.statusText;
  }
  return '';
}

// Strip the dynamic parts of an error message so events that differ only by
// per-request identifiers fingerprint to the same group. Conservative on
// purpose: status is the primary key, this just stops IDs from fragmenting an
// otherwise-identical message. Order matters — broader patterns run last.
function normalizeErrorMessage(message: string): string {
  return (
    message
      // Parenthesised HTTP status suffix, e.g. "… already exists. (400)." — the
      // status is already a fingerprint key and isn't always present, so drop
      // it (with any leading space and trailing dot) to avoid splitting the
      // same error on its presence/absence. Anchored to the end so a
      // parenthesised 3-digit run elsewhere in the message is left intact.
      .replace(/\s*\(\d{3}\)\.?$/, '')
      // Email addresses.
      .replace(/[^\s@]+@[^\s@]+\.[^\s@]+/g, '<email>')
      // UUIDs.
      .replace(
        /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi,
        '<uuid>',
      )
      // Prefixed resource IDs — Medusa ULIDs (cart_01J…) and Stripe IDs
      // (cus_Udcl…). Requires a 10+ char alphanumeric tail so short tokens like
      // the `pp_stripe_stripe` provider id survive as a discriminator.
      .replace(/\b[a-z]{2,12}_[A-Za-z0-9]{10,}\b/g, '<id>')
      // Bare long hex blobs (tokens, hashes).
      .replace(/\b[0-9a-f]{16,}\b/gi, '<hex>')
      // Bare long digit runs (numeric ids, timestamps).
      .replace(/\b\d{4,}\b/g, '<n>')
      .toLowerCase()
      .replace(/\s+/g, ' ')
      .trim()
      // Cap length so a pathological message can't blow up the fingerprint.
      .slice(0, 120)
  );
}

/**
 * Returns a Sentry fingerprint for a Medusa SDK / HTTP-shaped error, or `null`
 * for anything else (leave Sentry's default grouping in place).
 *
 * Shape: `['medusa-sdk', '<status>', '<normalized-message>']`. No
 * `{{ default }}` — see the file header for why.
 */
export function medusaSdkFingerprint(error: unknown): string[] | null {
  if (!isHttpLikeError(error)) {
    return null;
  }

  const status = String(error.status);
  const rawMessage = rawErrorMessage(error);
  const message = rawMessage ? normalizeErrorMessage(rawMessage) : '';

  return message ? ['medusa-sdk', status, message] : ['medusa-sdk', status];
}
